import moment, { DurationInputObject } from 'moment'
import CampaignFilter from '../back-end/src/models/gql/CampaignFilter'
import { IValidationResult } from '../types/IvalidationResult'
import {
  CampaignFilterTypeEnum,
  CampaignSearchableUserAttribute
} from './enums/campaigns'
import {
  ArrayValueOperators,
  IUserFilterComparator
} from '../back-end/src/models/base/UserFilterBase'
import { ATTRIBUTE_COMPARATOR_MAP } from './userFilterConstants'

/**
 * returns true when valid
 */
export const campaignFilterValidation = (
  filter: Partial<CampaignFilter>
): IValidationResult => {
  const failOnRefs = {
    message: `filter referencing other entities must have at least one item in ref_ids`,
    result: false
  }
  if (!filter.type) {
    return {
      message: `"type" filter property must be truthy`,
      result: false
    }
  }

  if (
    [
      CampaignFilterTypeEnum.group,
      CampaignFilterTypeEnum.workspace,
      CampaignFilterTypeEnum.topic_enrolment,
      CampaignFilterTypeEnum.pathway_enrolment,
      CampaignFilterTypeEnum.certification_enrolment
    ].includes(filter.type)
  ) {
    if (!filter.ref_ids || filter.ref_ids.length === 0) {
      return failOnRefs
    }
  } else if (filter.type === CampaignFilterTypeEnum.value_comparison) {
    if (
      !filter.comparator ||
      !filter.comparator.field ||
      !filter.comparator.operator ||
      (filter.comparator.operator !== ATTRIBUTE_COMPARATOR_MAP.is_empty &&
        filter.comparator.value === '')
    ) {
      return {
        message: `"value_comparison" filter must have a comparator field, operator and value`,
        result: false
      }
    }

    if (!(filter.comparator.field in CampaignSearchableUserAttribute)) {
      return {
        message: `field "${filter.comparator.field}" can not be used as campaign filter`,
        result: false
      }
    }

    if (Object.keys(ArrayValueOperators).includes(filter.comparator.operator)) {
      // IN, NOT IN and CONTAINS operators requires values to be Array
      if (
        !Array.isArray(filter.comparator.value) ||
        !filter.comparator.value.length
      ) {
        return {
          message: `"value_comparison" filter value must be array for "is", "is not" and "contains" filters`,
          result: false
        }
      }
    } else {
      // Other operators require values to be string except "IS EMPTY" filter. That one does not require value to be set.
      if (
        filter.comparator.operator !== 'is_empty' &&
        typeof filter.comparator.value !== 'string'
      ) {
        return {
          message: `"value_comparison" filter value must be string`,
          result: false
        }
      }
    }
  } else if (filter.type === CampaignFilterTypeEnum.duration_comparison) {
    const { comparator } = filter as {
      comparator: IUserFilterComparator
    }
    if (
      typeof comparator.value !== 'object' ||
      moment.duration(comparator.value as DurationInputObject).asSeconds() < 0
    ) {
      return {
        message: `"duration_comparison" filter value must be non negative`,
        result: false
      }
    }
  }
  if (filter.type === CampaignFilterTypeEnum.event_invitation) {
    // Event filter setup object must be present
    if (!filter.eventFilterSetup) {
      return {
        message:
          'Wrong event filter setup, please try again. Event Filter setup is missing.',
        result: false
      }
    }
    // Either recurringEventIds or eventIds must be set.
    if (
      filter.eventFilterSetup.recurringEventIds.length === 0 &&
      filter.eventFilterSetup.eventIds.length === 0
    ) {
      return {
        message:
          'No event or recurring events were selected in event filter. Please pick some events.',
        result: false
      }
    }

    if (filter.temporalComparator) {
      return {
        message: 'Event filters can not be combined with date filters.',
        result: false
      }
    }
  }

  if (
    (
      [
        CampaignFilterTypeEnum.pathway_enrolment,
        CampaignFilterTypeEnum.topic_enrolment,
        CampaignFilterTypeEnum.certification_enrolment
      ] as CampaignFilterTypeEnum[]
    ).includes(filter.type) &&
    !filter.enrolmentFilterSetup
  ) {
    return {
      message:
        'Wrong enrolment filter setup, please try again. Enrolment filter setup is missing.',
      result: false
    }
  }

  return { result: true, message: '' }
}
